import React from 'react';
import ProfileImage from '../../Header/ProfileImage';
import { getInitials } from '../../shared/TailwindComponents/CollaboratorsBadge';
import { normalise } from '../../shared/access/accessModel';

/**
 * Enriched user shape embedded on economics rows (see MATTER_ECONOMICS_API.md
 * "User fields on economics rows"): `{ user_id, user_uuid, name, email,
 * avatar_url, initials }`. All fields optional so the cell degrades gracefully
 * when the backend can't resolve a user.
 */
export interface UserCellUser {
  user_uuid?: string | null;
  user_id?: number | string | null;
  name?: string | null;
  /** Some endpoints (firm per_user) also keep a `user_name` alias. */
  user_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  initials?: string | null;
  /** Other endpoints feeding this cell spell the picture differently. */
  [key: string]: any;
}

interface UserCellProps {
  user?: UserCellUser | null;
  /** Shown when the row carries no resolvable name (e.g. legacy rows). */
  fallbackLabel?: string;
  /** Override the hover tooltip (defaults to email → name). */
  tooltip?: React.ReactNode;
}

/**
 * Flatten a tooltip node to plain text for the native `title` attribute - the
 * cell carries its own hover text rather than pulling MUI's Tooltip into a
 * shared economics primitive. `<br />` becomes a newline, which `title`
 * renders as a line break.
 */
const toTitle = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toTitle).join('');
  if (React.isValidElement(node)) {
    if (node.type === 'br') return '\n';
    return toTitle((node.props as { children?: React.ReactNode }).children);
  }
  return '';
};

/**
 * Reusable avatar + name cell for every economics view that shows a user (FIX 1,
 * #2274/#2278). Avatar falls back to initials → Gravatar via ProfileImage; the
 * email surfaces on hover. Replaces the old raw "User <id>" rendering.
 */
const UserCell: React.FC<UserCellProps> = ({ user, fallbackLabel, tooltip }) => {
  const name =
    user?.name ||
    user?.user_name ||
    user?.email ||
    fallbackLabel ||
    (user?.user_id != null ? `User ${user.user_id}` : '—');
  const initials = user?.initials || getInitials(name);
  const email = user?.email || '';
  // Not `user.avatar_url` alone: the time-entry and billing-rate rows carry the
  // picture as `profile_picture`, and reading one key left those cells on a
  // grey silhouette while the same person had a face everywhere else.
  const avatar = user ? normalise(user).avatar : null;

  return (
    <div className="flex items-center gap-2 min-w-0" title={toTitle(tooltip) || email || name}>
      <ProfileImage
        profilePicture={avatar}
        email={email || null}
        initials={initials}
        name={name}
        size="h-6 w-6"
      />
      <span className="truncate text-sm text-fg-strong">{name}</span>
    </div>
  );
};

export default UserCell;
